import { useState, useCallback, useEffect } from 'react';

export interface GuideState {
  step: number;       // current active step (0-4)
  visited: number[];  // steps the user has clicked the CTA on
  completed: boolean; // all 5 steps visited
  open: boolean;      // panel visible
}

const DEFAULT_STATE: GuideState = { step: 0, visited: [], completed: false, open: false };

function getKey(categoryId: string) {
  return `bip_setup_guide_${categoryId}`;
}

export function useSetupGuide(categoryId: string | undefined) {
  const key = categoryId ? getKey(categoryId) : null;

  const [state, setState] = useState<GuideState>(() => {
    if (!key) return DEFAULT_STATE;
    try {
      const saved = localStorage.getItem(key);
      return saved ? { ...DEFAULT_STATE, ...JSON.parse(saved) } : DEFAULT_STATE;
    } catch {
      return DEFAULT_STATE;
    }
  });

  // Persist on every change
  useEffect(() => {
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  const open = useCallback(() => setState(s => ({ ...s, open: true })), []);
  const close = useCallback(() => setState(s => ({ ...s, open: false })), []);

  const goToStep = useCallback((step: number) =>
    setState(s => ({ ...s, step: Math.max(0, Math.min(4, step)) })), []);

  const next = useCallback(() =>
    setState(s => ({ ...s, step: Math.min(4, s.step + 1) })), []);

  const prev = useCallback(() =>
    setState(s => ({ ...s, step: Math.max(0, s.step - 1) })), []);

  /** Mark the current step as visited (user clicked the CTA) */
  const visitStep = useCallback((step: number) =>
    setState(s => {
      const visited = s.visited.includes(step) ? s.visited : [...s.visited, step];
      const completed = visited.length >= 5;
      return { ...s, visited, completed };
    }), []);

  const complete = useCallback(() =>
    setState(s => ({ ...s, completed: true, open: false })), []);

  const reset = useCallback(() => setState(DEFAULT_STATE), []);

  return { state, open, close, goToStep, next, prev, visitStep, complete, reset };
}
